import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Drawer,
  List,
  ListItem,
  Typography,
  Box,
  Stack,
  Pagination,
  Container,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Skeleton,
  Badge,
  InputAdornment
} from '@mui/material';
import { 
  ShoppingCart as ShoppingCartIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingBag as ShoppingBagIcon,
  Search as SearchIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { API_BASE_URL, authenticatedFetch } from "../config/api";
import SimpleImage from './SimpleImage';
import { trackAddToCart } from '../services/metaPixel';
import "./Store.css";

const SkipLink = () => (
  <a href="#main-content" className="skip-link">
    تخطي إلى المحتوى الرئيسي
  </a>
);

const ErrorBoundary = ({ error, retry }) => (
  <Paper className="error-boundary">
    <Typography variant="h6" color="error" gutterBottom>
      خطأ في تحميل البيانات
    </Typography>
    <Typography variant="body2" color="textSecondary" paragraph>
      {error || "حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى."}
    </Typography>
    <Button variant="outlined" onClick={retry}>
      إعادة المحاولة
    </Button>
  </Paper>
);

const LoadingSkeleton = () => (
  <Grid container spacing={1} className="products-grid-container">
    {[...Array(12)].map((_, index) => (
      <Grid item xs={4} sm={4} md={3} lg={2} key={index}>
        <Card className="product-skeleton">
          <Skeleton variant="rectangular" height={200} />
          <CardContent>
            <Skeleton variant="text" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rectangular" height={32} sx={{ mt: 1 }} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const WholesaleStore = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastLocationSearch = useRef(location.search);
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("wholesaleCart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Error loading wholesale cart from localStorage:", error);
      return [];
    }
  });
  const [filter, setFilter] = useState({ brand_id: "", category_id: "", search: "" });
  const [searchQuery, setSearchQuery] = useState(""); // For debounced search
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [cartKey, setCartKey] = useState(0);
  const [isUrlProcessed, setIsUrlProcessed] = useState(false);

  // Removed minimum quantities for wholesale - now flexible

  // Fisher-Yates shuffle algorithm for random product ordering
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const showMessage = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== filter.search) {
        handleSearch(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only load data if URL has been processed to avoid premature API calls
    if (isUrlProcessed) {
      loadData();
    }
  }, [filter, page, isUrlProcessed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadFiltersData();
    // Cart is now initialized directly from localStorage in useState
  }, []);

  // -- THIS IS THE ONLY PLACE THAT CHANGES FILTER STATE --
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category') || searchParams.get('category_id');
    const brandParam = searchParams.get('brand') || searchParams.get('brand_id'); // Support both brand and brand_id parameters
    const searchParam = searchParams.get('search');
    
    let validCategoryId = "";
    if (categoryParam) {
      if (categories.length > 0) {
        const categoryExists = categories.some(cat => String(cat.id) === String(categoryParam));
        if (categoryExists) {
          validCategoryId = String(categoryParam);
        }
      } else {
        validCategoryId = String(categoryParam);
      }
    }
    let validBrandId = "";
    if (brandParam) {
      validBrandId = String(brandParam);
    }
    const newFilter = {
      category_id: validCategoryId,
      brand_id: validBrandId,
      search: searchParam || ""
    };
    
    setFilter(newFilter);
    setSearchQuery(searchParam || "");
    setPage(1);
    setIsUrlProcessed(true); // Mark URL as processed so API calls can proceed
  }, [location.search]);

  // Validate category filter when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && filter.category_id) {
      const categoryExists = categories.some(cat => String(cat.id) === String(filter.category_id));
      if (!categoryExists) {
        setFilter(prev => ({ ...prev, category_id: "" }));
      }
    }
  }, [categories, filter.category_id]);

  useEffect(() => {
    if (cart.length >= 0) {
      localStorage.setItem("wholesaleCart", JSON.stringify(cart));
      setCartKey(prev => prev + 1);
      window.dispatchEvent(new CustomEvent('wholesaleCartUpdated', { 
        detail: { cartItems: cart, cartLength: cart.length }
      }));
    }
  }, [cart]);

  const loadFiltersData = async () => {
    try {
      setError(null);
      const brandsResponse = await fetch(`${API_BASE_URL}/api/brands.php`);
      if (brandsResponse.ok) {
        const brandsData = await brandsResponse.json();
        if (Array.isArray(brandsData)) {
          setBrands(brandsData);
        } else if (brandsData.brands && Array.isArray(brandsData.brands)) {
          setBrands(brandsData.brands);
        } else if (brandsData.data && Array.isArray(brandsData.data)) {
          setBrands(brandsData.data);
        } else {
          const productsResponse = await authenticatedFetch(`${API_BASE_URL}/api/products.php?all=true`);
          const productsData = await productsResponse.json();
          if (productsData && productsData.products && Array.isArray(productsData.products)) {
            const brandsMap = new Map();
            productsData.products.forEach(product => {
              if (product.brand_id && product.brand_name) {
                brandsMap.set(product.brand_id, {
                  id: product.brand_id,
                  name: product.brand_name.trim()
                });
              } else if (product.brand && product.brand.trim()) {
                brandsMap.set(product.brand, {
                  id: product.brand,
                  name: product.brand.trim()
                });
              }
            });
            const brandsArray = Array.from(brandsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
            setBrands(brandsArray);
          }
        }
      }
      const categoriesResponse = await fetch(`${API_BASE_URL}/api/categories.php`);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
      }
    } catch (error) {
      setBrands([]);
      setCategories([]);
      setError("خطأ في تحميل البيانات المساعدة");
      showMessage("خطأ في تحميل البيانات المساعدة", "warning");
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        per_page: 'all', // Fetch all products to filter for wholesale
        ...(filter.category_id && { category_id: filter.category_id }),
        ...(filter.search && { search: filter.search }),
        ...(filter.brand_id && { brand: filter.brand_id })
      });
      const apiUrl = `${API_BASE_URL}/api/products.php?${params}`;
      
      const response = await authenticatedFetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      
      if (data.products) {
        // Filter only active products with wholesale prices
        const wholesaleProducts = data.products.filter(product => 
          product.active === 1 && 
          product.wholesale && 
          parseFloat(product.wholesale) > 0
        );
        
        // Shuffle products for random ordering each time store is loaded
        const shuffledProducts = shuffleArray(wholesaleProducts);
        
        // Client-side pagination
        const itemsPerPage = 12;
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedProducts = shuffledProducts.slice(startIndex, endIndex);
        
        setProducts(paginatedProducts);
        setTotal(shuffledProducts.length); // Total wholesale products for pagination
      } else {
        setProducts([]);
        setTotal(0);
      }
    } catch (error) {
      let errorMessage = "خطأ في الاتصال بالخادم";
      if (error.message.includes('HTTP 404')) {
        errorMessage = "لم يتم العثور على ملف المنتجات في الخادم";
      } else if (error.message.includes('HTTP 500')) {
        errorMessage = "خطأ في الخادم، يرجى المحاولة لاحقاً";
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = "فشل في الاتصال بالخادم، تحقق من الاتصال بالإنترنت";
      } else if (error.message.includes('CORS')) {
        errorMessage = "خطأ في إعدادات الخادم (CORS)";
      }
      setError(errorMessage);
      showMessage(errorMessage, "error");
      setProducts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query) => {
    setPage(1);
    const searchParams = new URLSearchParams(location.search);
    if (query) {
      searchParams.set('search', query);
    } else {
      searchParams.delete('search');
    }
    navigate(`/wholesale?${searchParams.toString()}`, { replace: true });
  };

  const handleCategoryChange = (categoryId) => {
    setPage(1);
    const searchParams = new URLSearchParams(location.search);
    if (categoryId) {
      searchParams.set('category', categoryId);
    } else {
      searchParams.delete('category');
      searchParams.delete('category_id');
    }
    navigate(`/wholesale?${searchParams.toString()}`, { replace: true });
  };

  const handleBrandChange = (brandId) => {
    setPage(1);
    const searchParams = new URLSearchParams(location.search);
    if (brandId) {
      searchParams.set('brand', brandId);
    } else {
      searchParams.delete('brand');
    }
    navigate(`/wholesale?${searchParams.toString()}`, { replace: true });
  };

  const handlePageChange = (event, newPage) => {
    // Update page state
    setPage(newPage);
    
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPrice = (product) => {
    return parseFloat(product.wholesale) || 0;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-IQ').format(amount);
  };

  const addToCart = (product, quantity = 1) => {
    const existingItemIndex = cart.findIndex(item => 
      (item.id && item.id === product.id) || 
      (item.product_id && item.product_id === product.id)
    );
    let newCart;

    if (existingItemIndex > -1) {
      newCart = cart.map((item, index) =>
        index === existingItemIndex
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      // Get brand name - use same logic as product card display
      const brandName = product.brand_id
        ? getBrandName(product.brand_id) || product.brand || ''
        : product.brand || '';

      newCart = [...cart, {
        id: product.id,
        product_id: product.id, // Add both for compatibility
        name: product.name,
        price: parseFloat(product.wholesale), // wholesale price
        quantity: quantity,
        image: product.image,
        weight: product.weight || 0,
        sku: product.sku || product.SKU || product.product_sku || '',
        brand: brandName,
        brand_id: product.brand_id || '',
        brand_name: brandName, // Add explicit brand_name field for backend
        type: 'wholesale'
      }];
    }

    setCart(newCart);
    setError('');
    
    // Track add to cart with Meta Pixel for wholesale
    const trackingPrice = parseFloat(product.wholesale) || 0;
    trackAddToCart({
      id: String(product.id),
      name: product.name,
      price: trackingPrice
    });
    
    showMessage(`تم إضافة ${quantity} من ${product.name} إلى سلة الجملة`);
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const newCart = cart.map(item =>
      (item.id === productId || item.product_id === productId)
        ? { ...item, quantity: newQuantity }
        : item
    );

    setCart(newCart);
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => 
      item.id !== productId && item.product_id !== productId
    );
    setCart(newCart);
    showMessage("تم حذف المنتج من السلة");
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('wholesaleCart');
    showMessage("تم تفريغ السلة");
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : '';
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => String(b.id) === String(brandId));
    return brand ? brand.name : '';
  };

  const renderActiveFilters = () => {
    const activeFilters = [];
    if (filter.search) {
      activeFilters.push({
        label: `البحث: ${filter.search}`,
        key: 'search',
        onRemove: () => {
          setPage(1);
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete('search');
          navigate(`/wholesale?${searchParams.toString()}`, { replace: true });
        }
      });
    }
    if (filter.brand_id) {
      const brandName = getBrandName(filter.brand_id);
      if (brandName) {
        activeFilters.push({
          label: `الماركة: ${brandName}`,
          key: 'brand',
          onRemove: () => {
            setPage(1);
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('brand');
            navigate(`/wholesale?${searchParams.toString()}`, { replace: true });
          }
        });
      }
    }
    if (filter.category_id) {
      const categoryName = getCategoryName(parseInt(filter.category_id));
      if (categoryName) {
        activeFilters.push({
          label: `التصنيف: ${categoryName}`,
          key: 'category',
          onRemove: () => {
            setPage(1);
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('category');
            searchParams.delete('category_id');
            navigate(`/wholesale?${searchParams.toString()}`, { replace: true });
          }
        });
      }
    }
    if (activeFilters.length === 0) return null;
    return (
      <Box className="filter-chips-container">
        {activeFilters.map(filter => (
          <Chip
            key={filter.key}
            label={filter.label}
            onDelete={filter.onRemove}
            className="filter-chip"
            size="small"
          />
        ))}
      </Box>
    );
  };

  const renderProductCard = (product) => (
    <Grid item xs={4} sm={4} md={3} lg={2} key={product.id}>
      <Box
        sx={{
          height: '100%',
          maxWidth: { xs: '100%', sm: '250px', md: '220px', lg: '200px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          p: 0,
          position: 'relative',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-4px)',
            transition: 'transform 0.2s ease-in-out'
          }
        }}
        className="product-card-optimized"
        onClick={() => navigate(`/product/${product.id}`)}
      >
        {/* Wholesale Badge */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
            backgroundColor: '#2E7D32',
            color: 'white',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.7rem',
            fontWeight: 'bold'
          }}
        >
          جملة
        </Box>

        {product.stock !== undefined && (
          <Box
            className={`stock-indicator ${
              product.stock > 10 ? 'in-stock' : 
              product.stock > 0 ? 'low-stock' : 'out-of-stock'
            }`}
          >
            {product.stock > 10 ? 'متوفر' : 
             product.stock > 0 ? `${product.stock} متبقي` : 'نفد المخزون'}
          </Box>
        )}

        <SimpleImage
          src={product.image && typeof product.image === 'string' && product.image.trim() ? 
            (product.image.startsWith('http') ? 
              product.image : 
              `https://al-sadik.com/api/uploads/products/${product.image.replace('uploads/', '')}`
            ) : '/placeholder-image.jpg'
          }
          alt={product.name || 'Product image'}
          aspectRatio="1"
          sx={{
            width: '100%',
            borderRadius: 0,
            mb: 1
          }}
        />
        <Box sx={{ width: '100%', textAlign: 'center', px: 1, pb: 1 }}>
          <Typography variant="caption" sx={{ color: 'grey.600', fontSize: '0.85rem', mb: 0.5, display: 'block', fontWeight: 400 }}>
            {product.brand_id
              ? getBrandName(product.brand_id) || product.brand || ''
              : product.brand || ''}
          </Typography>
          <Typography variant="h6" component="h3" sx={{
            fontWeight: 'bold',
            fontSize: { xs: '0.9rem', sm: '1rem' },
            mb: 0.5,
            height: '2.2em',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.1,
            wordBreak: 'break-word',
            color: 'grey.700'
          }}>
            {product.name}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              color: '#2E7D32',
              mb: 1,
              height: '1.4em',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }}
          >
            {formatCurrency(getPrice(product))} د.ع
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            سعر الجملة
          </Typography>
          
          {/* Two Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            {/* Add Single Item Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (e.currentTarget && e.currentTarget.classList) {
                  e.currentTarget.classList.add('clicked');
                  setTimeout(() => {
                    if (e.currentTarget && e.currentTarget.classList) {
                      e.currentTarget.classList.remove('clicked');
                    }
                  }, 600);
                }
                addToCart(product, 1);
              }}
              disabled={product.stock === 0}
              className="add-to-cart-feedback"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                py: 0.75,
                px: 2,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: '#3b5dac',
                color: '#3b5dac',
                backgroundColor: 'transparent',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                '&:hover': {
                  borderColor: '#2a406e',
                  color: '#2a406e',
                  backgroundColor: 'rgba(59, 93, 172, 0.04)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e',
                  backgroundColor: 'transparent'
                },
                transition: 'all 0.3s ease'
              }}
            >
              إضافة قطعة واحدة
            </Button>
            
            {/* Add Box Quantity Button */}
            {product.quantity_per_box && parseInt(product.quantity_per_box) > 1 && (
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (e.currentTarget && e.currentTarget.classList) {
                    e.currentTarget.classList.add('clicked');
                    setTimeout(() => {
                      if (e.currentTarget && e.currentTarget.classList) {
                        e.currentTarget.classList.remove('clicked');
                      }
                    }, 600);
                  }
                  addToCart(product, parseInt(product.quantity_per_box));
                }}
                disabled={product.stock === 0}
                className="add-to-cart-feedback"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  py: 0.75,
                  px: 2,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  backgroundColor: '#3b5dac',
                  color: '#fff',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    transform: 'translateY(-1px)',
                    backgroundColor: '#2a406e',
                    color: '#fff',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                إضافة كرتون ({parseInt(product.quantity_per_box)} قطعة)
              </Button>
            )}
            
            {/* Fallback button if no quantity_per_box */}
            {(!product.quantity_per_box || parseInt(product.quantity_per_box) <= 1) && (
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (e.currentTarget && e.currentTarget.classList) {
                    e.currentTarget.classList.add('clicked');
                    setTimeout(() => {
                      if (e.currentTarget && e.currentTarget.classList) {
                        e.currentTarget.classList.remove('clicked');
                      }
                    }, 600);
                  }
                  addToCart(product, 1);
                }}
                disabled={product.stock === 0}
                className="add-to-cart-feedback"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  py: 0.75,
                  px: 2,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  backgroundColor: '#3b5dac',
                  color: '#fff',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    transform: 'translateY(-1px)',
                    backgroundColor: '#2a406e',
                    color: '#fff',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {product.stock === 0 ? 'نفد المخزون' : 'أضف للسلة'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Grid>
  );

  const handleCheckout = () => {
    if (cart.length === 0) {
      setError('السلة فارغة');
      showMessage('السلة فارغة', 'error');
      return;
    }
    // Navigate to wholesale checkout
    navigate('/wholesale/checkout', { state: { cartItems: cart } });
  };

  const CartDrawer = () => (
    <Drawer
      anchor="right"
      open={isCartOpen}
      onClose={() => setIsCartOpen(false)}
      key={`wholesale-cart-drawer-${cartKey}`}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          maxWidth: '400px'
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            سلة الجملة ({getTotalItems()})
          </Typography>
          <Button onClick={() => setIsCartOpen(false)}>
            إغلاق
          </Button>
        </Box>
        
        {cart && cart.length > 0 ? (
          <>
            <List key={`wholesale-cart-list-${cartKey}`} sx={{ maxHeight: 400, overflow: 'auto' }} className="cart-items-list">
              {cart.map((item, index) => {
                if (!item || (!item.id && !item.product_id)) {
                  return null;
                }
                
                const itemId = item.id || item.product_id;
                const price = item.price || 0;
                const displayPrice = typeof price === "number" && !isNaN(price) ? price : 0;
                
                return (
                  <ListItem 
                    key={`item-${itemId}-${index}`}
                    divider
                    sx={{ py: 2, px: 1 }}
                  >
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                      {/* Product Image */}
                      <SimpleImage
                        src={item.image && typeof item.image === 'string' && item.image.trim() ? 
                          (item.image.startsWith('http') ? 
                            item.image : 
                            `https://al-sadik.com/api/uploads/products/${item.image.replace('uploads/', '')}`
                          ) : '/placeholder-image.jpg'
                        }
                        alt={item.name || 'Product image'}
                        aspectRatio="1"
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          mr: 2,
                          backgroundColor: 'grey.100'
                        }}
                      />
                      
                      {/* Product Details */}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          الكمية: {item.quantity} × {formatCurrency(displayPrice)} د.ع
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                          المجموع: {formatCurrency(displayPrice * item.quantity)} د.ع
                        </Typography>
                      </Box>
                      
                      {/* Quantity Controls */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        backgroundColor: 'white',
                        mr: 1
                      }}>
                        <IconButton 
                          size="small"
                          onClick={() => updateCartQuantity(itemId, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          sx={{ borderRadius: 0 }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography 
                          sx={{ 
                            mx: 1, 
                            minWidth: '30px', 
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '0.9rem'
                          }}
                        >
                          {item.quantity}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => updateCartQuantity(itemId, item.quantity + 1)}
                          sx={{ borderRadius: 0 }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      {/* Delete Button */}
                      <IconButton 
                        size="small" 
                        onClick={() => removeFromCart(itemId)}
                        sx={{ 
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.lighter'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }} className="cart-total-container">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">الإجمالي:</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }} className="cart-total-text">
                    {formatCurrency(getTotalPrice())} د.ع
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    إجمالي الكمية: {getTotalItems()} قطعة
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={1}>
                <Button 
                  variant="contained" 
                  fullWidth 
                  onClick={() => {
                    setIsCartOpen(false);
                    handleCheckout();
                  }}
                  disabled={cart.length === 0}
                  className="checkout-btn"
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    backgroundColor: '#3b5dac',
                    color: '#fff',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      transform: 'translateY(-1px)',
                      backgroundColor: '#2a406e',
                      color: '#fff',
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  إتمام طلب الجملة
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={clearCart}
                  disabled={cart.length === 0}
                  className="empty-cart-btn"
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    fontWeight: 500,
                    textTransform: 'none',
                    borderColor: '#3b5dac',
                    color: '#3b5dac',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      borderColor: '#2a406e',
                      color: '#2a406e',
                      backgroundColor: 'rgba(59, 93, 172, 0.04)'
                    },
                    '&:disabled': {
                      borderColor: '#e0e0e0',
                      color: '#9e9e9e'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  تفريغ السلة
                </Button>
              </Stack>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }} className="cart-empty-container">
            <ShoppingCartIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
            <Typography color="text.secondary">
              سلة الجملة فارغة
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              اختر المنتجات بالكمية المناسبة لك
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );

  const retryLoad = () => {
    setError(null);
    loadData();
    loadFiltersData();
  };

  // Generate JSON-LD structured data for the wholesale store page
  const generateWholesaleStoreJsonLd = () => {
    return {
      "@context": "https://schema.org",
      "@type": "Store",
      "name": "Al Sadik Wholesale Store",
      "description": "متجر الصادق للجملة - منتجات بأسعار الجملة",
      "url": window.location.href,
      "currenciesAccepted": "IQD",
      "paymentAccepted": "Cash",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Al Sadik Wholesale Product Catalog",
        "itemListElement": products.slice(0, 20).map(product => {
          const price = product.wholesale || product.sell_price || 0;
          const hasValidPrice = price && price > 0;
          
          return {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": product.name || "منتج",
              "description": product.description || product.name || "منتج من متجر الصادق للجملة",
              "productID": product.id.toString(),
              "image": product.image ? (
                product.image.startsWith('http') ? 
                  product.image : 
                  product.image.startsWith('api/uploads/products/') ? 
                    `${API_BASE_URL}/${product.image}` : 
                    `https://al-sadik.com/api/uploads/products/${product.image}`
              ) : '',
              "brand": {
                "@type": "Brand",
                "name": product.brand || "الصادق"
              },
              "category": product.category_name || "General"
            },
            "price": hasValidPrice ? price.toString() : "0",
            "priceCurrency": "IQD",
            "availability": product.quantity > 0 
              ? "https://schema.org/InStock" 
              : "https://schema.org/OutOfStock",
            "itemCondition": "https://schema.org/NewCondition",
            "seller": {
              "@type": "Organization",
              "name": "متجر الصادق للجملة"
            }
          };
        })
      }
    };
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 0, sm: 2, md: 3 } }}>
      {/* JSON-LD Structured Data for Wholesale Store */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateWholesaleStoreJsonLd())
        }}
      />
      
      <SkipLink />
      
      {/* Wholesale Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
              متجر الجملة
            </Typography>
            <Typography variant="body1" color="text.secondary">
              أسعار خاصة للتجار والموزعين - خيارات متنوعة للكمية
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }} className="filters-container">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="البحث في منتجات الجملة..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>الماركة</InputLabel>
              <Select
                value={filter.brand_id && brands.some(brand => String(brand.id) === String(filter.brand_id)) ? filter.brand_id : ""}
                label="الماركة"
                onChange={(e) => handleBrandChange(e.target.value)}
              >
                <MenuItem value="">جميع الماركات</MenuItem>
                {brands.map(brand => (
                  <MenuItem key={brand.id} value={brand.id.toString()}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>التصنيف</InputLabel>
              <Select
                value={filter.category_id && categories.some(category => String(category.id) === String(filter.category_id)) ? filter.category_id : ""}
                label="التصنيف"
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <MenuItem value="">جميع التصنيفات</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setIsCartOpen(true)}
              disabled={cart.length === 0}
              startIcon={
                <Badge badgeContent={getTotalItems()} color="error">
                  <ShoppingCartIcon />
                </Badge>
              }
              className="add-to-cart-feedback"
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                py: 1,
                px: 2,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: '#3b5dac',
                color: '#fff',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  transform: 'translateY(-1px)',
                  backgroundColor: '#2a406e',
                  color: '#fff',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                },
                transition: 'all 0.3s ease'
              }}
            >
              السلة
            </Button>
          </Grid>
        </Grid>
        {renderActiveFilters()}
      </Paper>

      <main id="main-content">
        {error && (
          <ErrorBoundary error={error} retry={retryLoad} />
        )}
        {isLoading ? (
          <LoadingSkeleton />
        ) : products.length === 0 && !error ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              لا توجد منتجات جملة متوفرة
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filter.search || filter.brand_id || filter.category_id 
                ? "جرب تعديل البحث أو المرشحات" 
                : "لم يتم العثور على منتجات جملة في المتجر حالياً"}
            </Typography>
            {(filter.search || filter.brand_id || filter.category_id) && (
              <Button 
                variant="outlined" 
                onClick={() => {
                  setPage(1);
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.delete('search');
                  searchParams.delete('brand');
                  searchParams.delete('category');
                  searchParams.delete('category_id');
                  navigate(`/wholesale?${searchParams.toString()}`, { replace: true });
                }}
              >
                مسح جميع المرشحات
              </Button>
            )}
          </Paper>
        ) : !error && (
          <>
            <Grid container spacing={1} className="products-grid-container">
              {products.map(renderProductCard)}
            </Grid>
            {total > 12 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={Math.ceil(total / 12)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </main>

      <CartDrawer />
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WholesaleStore;
